/**
 * Realtime subscriptions.
 *
 * Realtime streams raw table rows, not the assembled `order_json` payloads the
 * screens render. Rather than maintain a second, subtly different client-side
 * projection of an order, every subscriber here is a change *signal*: the app
 * refetches through the same RPC it used for the initial load. That keeps one
 * projection in the system — the database's — at the cost of one small request
 * per change, which is the right trade at restaurant order volumes.
 *
 * Supabase Realtime evaluates Row Level Security per subscriber, so a customer
 * only ever receives rows their policies already allow.
 */

import type { DbClient } from './client';

/** What changed, for callers that want to react differently to inserts. */
export interface OrderChangeSignal {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  /** Raw row; typed loosely because only `id` and `status` are relied upon. */
  row: Record<string, unknown> | null;
  old: Record<string, unknown> | null;
}

export interface SubscribeOptions {
  /** Called on every relevant change. */
  onChange: (signal: OrderChangeSignal) => void;
  /** Called when the channel connects, drops, or errors. */
  onStatus?: (status: 'connected' | 'disconnected' | 'error', detail?: string) => void;
}

/**
 * Subscribes to every order change for one restaurant.
 *
 * Used by the merchant queue and dashboard. Both `orders` and `order_items` are
 * watched, because adding a line to an existing order must also refresh it.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant to watch.
 * @param options Change and status handlers.
 * @returns An unsubscribe function that closes the channel.
 */
export function subscribeToRestaurantOrders(
  client: DbClient,
  restaurantId: string,
  options: SubscribeOptions,
): () => void {
  const channel = client
    .channel(`restaurant:${restaurantId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `restaurant_id=eq.${restaurantId}`,
      },
      (payload) => {
        options.onChange({
          eventType: payload.eventType as OrderChangeSignal['eventType'],
          row: (payload.new ?? null) as Record<string, unknown> | null,
          old: (payload.old ?? null) as Record<string, unknown> | null,
        });
      },
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'order_items',
        filter: `restaurant_id=eq.${restaurantId}`,
      },
      (payload) => {
        options.onChange({
          eventType: payload.eventType as OrderChangeSignal['eventType'],
          row: (payload.new ?? null) as Record<string, unknown> | null,
          old: (payload.old ?? null) as Record<string, unknown> | null,
        });
      },
    )
    .subscribe((status, error) => {
      if (!options.onStatus) return;
      if (status === 'SUBSCRIBED') options.onStatus('connected');
      else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        options.onStatus('error', error?.message);
      } else if (status === 'CLOSED') options.onStatus('disconnected');
    });

  return () => {
    void client.removeChannel(channel);
  };
}

/**
 * Subscribes to one order.
 *
 * This is the mechanism behind "the merchant scanned my QR, so my screen says
 * collected": a single row flipped to `completed` in the database.
 *
 * @param client A Supabase client.
 * @param orderId Order to watch.
 * @param options Change and status handlers.
 * @returns An unsubscribe function that closes the channel.
 */
export function subscribeToOrder(
  client: DbClient,
  orderId: string,
  options: SubscribeOptions,
): () => void {
  const channel = client
    .channel(`order:${orderId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'orders', filter: `id=eq.${orderId}` },
      (payload) => {
        options.onChange({
          eventType: payload.eventType as OrderChangeSignal['eventType'],
          row: (payload.new ?? null) as Record<string, unknown> | null,
          old: (payload.old ?? null) as Record<string, unknown> | null,
        });
      },
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'order_events', filter: `order_id=eq.${orderId}` },
      (payload) => {
        options.onChange({
          eventType: payload.eventType as OrderChangeSignal['eventType'],
          row: (payload.new ?? null) as Record<string, unknown> | null,
          old: (payload.old ?? null) as Record<string, unknown> | null,
        });
      },
    )
    .subscribe((status, error) => {
      if (!options.onStatus) return;
      if (status === 'SUBSCRIBED') options.onStatus('connected');
      else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        options.onStatus('error', error?.message);
      } else if (status === 'CLOSED') options.onStatus('disconnected');
    });

  return () => {
    void client.removeChannel(channel);
  };
}

/**
 * Subscribes to the merchant's own menu changes.
 *
 * Lets a second device at the counter see a price change made on the office
 * iPad without a reload.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant to watch.
 * @param onChange Called on any menu change.
 * @returns An unsubscribe function.
 */
export function subscribeToMenu(
  client: DbClient,
  restaurantId: string,
  onChange: () => void,
): () => void {
  const channel = client
    .channel(`menu:${restaurantId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'menu_items', filter: `restaurant_id=eq.${restaurantId}` },
      onChange,
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'menu_categories',
        filter: `restaurant_id=eq.${restaurantId}`,
      },
      onChange,
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'item_discounts',
        filter: `restaurant_id=eq.${restaurantId}`,
      },
      onChange,
    )
    .subscribe();

  return () => {
    void client.removeChannel(channel);
  };
}
